import React, { useState, useMemo } from "react";
import { Flex, Text, Button, Illustration, HelpIndicator } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { formatDate, addDays } from "../../../utils/date_formatter";
import { DefaultModal } from "../../common/DefaultModal";
import { Instructions } from "./Instructions";
import { OptionButton } from "./OptionButton";
import {
  Box,
  documentIcon,
  questionText,
  questionDescription,
  optionsContainer,
  actionButtonsContainer,
  disclaimerText,
  linkText,
  highlightText,
  emphasizeText,
} from "./styles_vanilla.css";
import { ProfileRefreshCardProps, RefreshOption } from "./types";

export const ProfileRefreshCard = ({
  orgName,
  accountType,
  lastRefreshDate,
  onContinue,
  onSkip,
  isLoading = false,
  isExpired = false,
}: ProfileRefreshCardProps) => {
  const [selectedOption, setSelectedOption] = useState<RefreshOption>(null);
  const [showSkipModal, setShowSkipModal] = useState(false);

  const dueDate = useMemo(() => addDays(lastRefreshDate, 30) ?? "--", [lastRefreshDate]);

  const helpContent = useMemo(() => {
    return <Instructions variant="dark" orgName={orgName} accountType={accountType} />;
  }, [accountType, orgName]);

  const handleOptionSelect = (option: RefreshOption) => {
    setSelectedOption(option);
  };

  const handleContinue = () => {
    onContinue(isExpired || selectedOption === "yes");
  };

  const handleSkipClick = () => {
    setShowSkipModal(true);
  };

  const handleSkipConfirm = () => {
    setShowSkipModal(false);
    onSkip();
  };

  const handleSkipCancel = () => {
    setShowSkipModal(false);
  };

  const isOptionSelected = (option: RefreshOption) => selectedOption === option;

  return (
    <>
      <Flex className={Box} flexDirection="column" alignItems="center">
        <Text variant="h3" className={questionText} align="center">
          Review {orgName}&apos;s information
        </Text>
        <Illustration name="account_overview" className={documentIcon} />
        <Flex justifyContent="center" alignItems="center"></Flex>
        {isExpired ? (
          <>
            <Text>
              Let&apos;s review if there were any changes to {orgName} since the last refresh on{" "}
              <Text asSpan variant="regular-lg" className={emphasizeText}>
                {formatDate(lastRefreshDate)}.
              </Text>
            </Text>
            <Instructions
              variant="light"
              orgName={orgName}
              accountType={accountType}
              className={optionsContainer}
            />
          </>
        ) : (
          <>
            <Text variant="regular-lg" align="center" className={questionDescription}>
              Were there any{" "}
              <HelpIndicator content={helpContent} id="changes-help" fullWidth>
                <Text asSpan variant="regular-lg" className={highlightText}>
                  changes
                </Text>
              </HelpIndicator>{" "}
              to {orgName} since the last refresh on{" "}
              <Text asSpan variant="regular-lg" className={emphasizeText}>
                {formatDate(lastRefreshDate)}
              </Text>
              ?
            </Text>
            <Flex
              className={optionsContainer}
              flexDirection="column"
              alignItems="center"
              gap={Sizes.MD}
            >
              <OptionButton
                name="Yes, there were changes"
                isSelected={isOptionSelected("yes")}
                onClick={() => handleOptionSelect("yes")}
              />

              <OptionButton
                name="No, there were no changes"
                isSelected={isOptionSelected("no")}
                onClick={() => handleOptionSelect("no")}
              />
            </Flex>
          </>
        )}
        <Flex className={actionButtonsContainer} justifyContent="center" gap={12}>
          <Button variant="neutral" onClick={handleSkipClick} disabled={isLoading}>
            Skip
          </Button>
          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={(!isExpired && !selectedOption) || isLoading}
            isLoading={isLoading}
          >
            Continue
          </Button>
        </Flex>
        {!isExpired && (
          <>
            <HelpIndicator content={helpContent} id="applicable-changes-help" fullWidth>
              <Text variant="regular-md" className={linkText} role="button">
                What are applicable changes?
              </Text>
            </HelpIndicator>
            <Text variant="regular-md" className={disclaimerText} align="center">
              Not sure? Select &quot;Yes&quot; to review the information on file.
            </Text>
          </>
        )}
      </Flex>
      <DefaultModal
        show={showSkipModal}
        title="Warning"
        body={
          <Flex flexDirection="column" gap={Sizes.SM}>
            <Text variant="regular-md">
              If you skip now, you can return to this workflow using the link in your email to
              complete this submission.
            </Text>
            <Text variant="regular-md">
              Please note:
              <br />
              This must be completed by <strong>{dueDate}</strong> in order to receive
              distributions.
            </Text>
          </Flex>
        }
        posBtnText="Continue to skip"
        negBtnText="Cancel"
        onClose={handleSkipCancel}
        onConfirm={handleSkipConfirm}
      />
    </>
  );
};
